/**
 * The faint architectural drawing used as a placeholder wherever a real drawing is not
 * available yet: the thumbnail slot of a B1 project card.
 *
 * It lives in `components/` rather than in `features/entrance/` because it began on the
 * entrance and a second caller arrived (KLQ-20260819-003, the project card), and a
 * cross-cutting object lands once (CLAUDE.md retro rule 8). Both of its bigger callers have
 * since moved on — the entrance and the landing hero carry `AxonometryDrawing` as of
 * 2026-09-12 — and the card is what it is left serving, which is the slot it is actually
 * right for: it survives being 120px wide, where a whole building does not.
 *
 * Geometry from `design/project/screens/overview.jsx`, which is reference geometry and not
 * implementation — so the shapes are carried over verbatim and the colours are not. That
 * prototype writes hex literals; here every stroke and fill is a token, applied through the
 * stylesheet, because `scripts/guard.mjs` fails the build on a hex in `src/` and because a
 * token copied as a value is a token that is now wrong in two places.
 *
 * **The pattern id is per-instance.** A B1 grid draws this once per card, and a document with
 * twelve elements sharing one `id` is invalid markup whose `url(#…)` references all resolve
 * to whichever copy the parser saw first. `useId` makes each one its own; the colons React
 * puts in that value are stripped, because a fragment identifier carrying them is a URL
 * reference nobody should have to reason about.
 *
 * `aria-hidden`: it is a mood, not information. A screen reader that announced "faint floor
 * plan geometry" would be reading out the wallpaper.
 */

import { useId } from 'react';

import styles from './FloorPlanPattern.module.css';

export function FloorPlanPattern() {
  const gridId = `kalqa-floorplan-${useId().replace(/:/g, '')}`;

  return (
    <svg
      className={styles.plan}
      viewBox="0 0 100 75"
      preserveAspectRatio="xMidYMid slice"
      aria-hidden
    >
      <defs>
        <pattern id={gridId} width="5" height="5" patternUnits="userSpaceOnUse">
          <path className={styles.grid} d="M5 0H0V5" />
        </pattern>
      </defs>
      <rect className={styles.paper} width="100" height="75" />
      <rect width="100" height="75" fill={`url(#${gridId})`} />
      {/* Outer wall */}
      <rect className={styles.wall} x="10" y="10" width="80" height="55" />
      {/* Internal walls */}
      <path
        className={styles.partition}
        d="M10 35 H40 M40 10 V50 M40 50 H75 M55 35 V65 M75 10 V40 M65 40 H90"
      />
      {/* Door swings */}
      <path className={styles.door} d="M25 35 A5 5 0 0 1 30 30" />
      <path className={styles.door} d="M40 18 A5 5 0 0 1 45 23" />
    </svg>
  );
}
